'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { Label } from '@/components/ui/label.component';
import { cn } from '@/utils/cn-tw-merge';

const checkboxStyles = tv({
  slots: {
    container: [
      'peer h-6 w-6 shrink-0 rounded-sm border border-gray-400 ring-offset-background focus-visible:outline-none',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
      'disabled:opacity-50 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500',
      'data-[state=checked]:text-primary-foreground',
    ],
    wrapper: 'flex items-center justify-center rounded-lg',
    root: 'flex items-center',
    label: 'text-base text-gray-700 cursor-pointer',
  },

  variants: {
    checked: {
      true: {
        label: 'font-semibold',
      },
      false: {
        label: 'font-normal',
      },
      indeterminate: {},
    },
    withBackground: {
      true: {
        wrapper: 'w-12 h-12 min-w-12 min-h-12 bg-green-50',
        root: 'gap-4',
      },
      false: {
        wrapper: '',
        root: 'gap-3',
        label: 'mt-1',
      },
    },
  },

  defaultVariants: {
    withBackground: true,
    checked: false,
  },
});

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    htmlFor?: string;
    withBackground?: boolean;
  }
>(({ className, htmlFor, withBackground = true, ...props }, ref) => {
  const { checked, id } = props;
  const isChecked = checked === true || checked === 'indeterminate';
  const { container, wrapper, root, label } = checkboxStyles({
    withBackground,
    checked: isChecked,
  });

  const labelClasses = label();
  const checkboxId = id || htmlFor;

  return (
    <div className={root()}>
      <div className={wrapper()}>
        <CheckboxPrimitive.Root ref={ref} className={container({ className })} {...props} id={checkboxId}>
          <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
            <Check className="h-4 w-4" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      </div>

      <Label htmlFor={checkboxId} className={labelClasses}>
        {props.children}
      </Label>
    </div>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
